import { useState, type FormEvent } from 'react';
import { useProjectStore } from '../stores/projectStore';
import { modalStyles as styles, toSlug } from './CreateProjectModal';

export function ImportProjectModal({ onClose }: { onClose: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [autoSlug, setAutoSlug] = useState(true);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  const { importProject } = useProjectStore();

  const handleNameChange = (val: string) => {
    setName(val);
    if (autoSlug) setSlug(toSlug(val));
  };

  const handleFile = (picked: File | null) => {
    setFile(picked);
    if (picked && !name) handleNameChange(picked.name.replace(/\.zip$/i, ''));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!file) return;
    setError('');
    setImporting(true);
    try {
      await importProject(file, name, slug);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import the game');
    }
    setImporting(false);
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <form style={styles.modal} onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit}>
        <div style={styles.title}>Import a Game</div>

        {error && <div style={styles.error}>{error}</div>}

        <div style={styles.field}>
          <label style={styles.label}>Game zip</label>
          <input
            style={styles.input}
            type="file"
            accept=".zip,application/zip"
            required
            onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
          />
          <div style={styles.hint}>{"A zip downloaded from a game's settings page."}</div>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Project Name</label>
          <input
            style={styles.input}
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            required
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Slug</label>
          <input
            style={styles.input}
            value={slug}
            onChange={(e) => {
              setAutoSlug(false);
              setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''));
            }}
            required
            minLength={3}
            maxLength={50}
            pattern="[a-z0-9][a-z0-9-]*[a-z0-9]"
          />
          <div style={styles.hint}>Used in URLs: /play/{slug || '...'}</div>
        </div>

        <div style={styles.actions}>
          <button style={styles.btn} type="button" onClick={onClose}>
            Cancel
          </button>
          <button style={styles.btnPrimary} type="submit" disabled={importing || !file}>
            {importing ? 'Importing...' : 'Import'}
          </button>
        </div>
      </form>
    </div>
  );
}
